<template>
  <div class="leversphere-loop-mobile-preview" v-if="loopCount > 1">
    <transition name="slide-down">
      <div class="preview-container" v-show="isVisible">
        <!-- Slider Row with buttons on both sides of the small image -->
        <div class="slider-row">
          <!-- Left Arrow -->
          <button type="button" class="nav-arrow left" @click="prevPack" :disabled="activePackIdx <= 1">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>

          <!-- Centered Small Image Wrapper -->
          <div class="small-image-wrapper" :class="{ 'has-watermark': isHasWatermark }" @click="zoomIn">
            <img :src="currentPreviewImage" alt="Preview" class="preview-image" />
            <div class="badge-title">
              {{ activePackIdx }}/{{ loopCount }}
            </div>
          </div>

          <!-- Right Arrow -->
          <button type="button" class="nav-arrow right" @click="nextPack" :disabled="activePackIdx >= loopCount">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>

        <!-- Sticky Loop Tabs Row -->
        <div class="customily-pack-tabs-wrapper">
          <div
            ref="tabsContainer"
            class="customily-pack-tabs"
          >
            <div
              v-for="packIdx in loopCount"
              :key="packIdx"
              class="pack-tab-item"
              :class="{ active: activePackIdx === packIdx }"
              @click="selectTab(packIdx)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
              <span>Item {{ (packIdx) }}</span>
            </div>
          </div>
          <div v-if="hasMoreTabs" class="customily-pack-tabs-more">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
            </svg>
            <div class="customily-pack-tabs-dropdown">
              <div
                v-for="packIdx in loopCount"
                :key="packIdx"
                class="dropdown-item"
                :class="{ active: activePackIdx === packIdx }"
                @click="selectTab(packIdx)"
              >
                Item {{ packIdx }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import useProductStore from '@/store/product'
import EventBus from '@/services/eventbus'
import { isProductHasTags } from '@/utils/product'

const productStore = useProductStore()

const isHasWatermark = computed(() => {
  const hasTag = isProductHasTags(productStore.product, ['watermark', 'leversphere_watermark'])
  if (hasTag && typeof window !== 'undefined') {
    if (!document.documentElement.style.getPropertyValue('--customily-watermark-image')) {
      const domain = window.location.hostname || window.location.host || 'hopesify.com'
      const capitalizedDomain = domain.charAt(0).toUpperCase() + domain.slice(1)
      const encodedDomain = encodeURIComponent(capitalizedDomain)
      const svg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Cstyle%3Etext %7B font-size: 22px; font-weight: bold; font-family: Arial, sans-serif; fill: rgba(0,0,0,0.13); text-anchor: middle; %7D%3C/style%3E%3Ctext transform='translate(75, 50) rotate(-30)'%3E${encodedDomain}%3C/text%3E%3Ctext transform='translate(225, 50) rotate(-30)'%3E${encodedDomain}%3C/text%3E%3Ctext transform='translate(150, 150) rotate(-30)'%3E${encodedDomain}%3C/text%3E%3Ctext transform='translate(0, 150) rotate(-30)'%3E${encodedDomain}%3C/text%3E%3Ctext transform='translate(300, 150) rotate(-30)'%3E${encodedDomain}%3C/text%3E%3C/svg%3E")`
      document.documentElement.style.setProperty('--customily-watermark-image', svg)
    }
  }
  return hasTag
})
const activePackIdx = ref(1)
const isVisible = ref(false)

const loopCount = computed(() => {
  const selectedOptions = productStore.selectedVariant?.selectedOptions
  const packOption = selectedOptions?.find((opt: any) => opt.name.toLowerCase() === 'pack')
  if (packOption) {
    const match = packOption.value.match(/\d+/)
    if (match) {
      return parseInt(match[0], 10)
    }
  }
  return 1
})

const currentPreviewImage = computed(() => {
  const customPreview = productStore.packPreviewImages?.[activePackIdx.value - 1]
  if (customPreview) return customPreview
  
  // fallback to product featured image or first media
  return productStore.product?.featuredImage?.src || productStore.product?.media?.[0]?.image?.src || ''
})

function prevPack() {
  if (activePackIdx.value > 1) {
    activePackIdx.value--
    EventBus.trigger('select-active-pack-idx', activePackIdx.value)
  }
}

function nextPack() {
  if (activePackIdx.value < loopCount.value) {
    activePackIdx.value++
    EventBus.trigger('select-active-pack-idx', activePackIdx.value)
  }
}

function handleClose() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function zoomIn() {
  EventBus.trigger('select-active-pack-idx', activePackIdx.value)
  setTimeout(() => {
    const btn = document.querySelector('.button-show-preview') as HTMLElement
    if (btn) btn.click()
  }, 100)
}

const tabsContainer = ref<HTMLElement | null>(null)
const hasMoreTabs = computed(() => loopCount.value > 3)

const handleSelectActivePackIdx = (idx: number) => {
  activePackIdx.value = idx
  scrollToTab(idx)
}

function scrollToTab(packIdx: number) {
  if (tabsContainer.value) {
    const tabEl = tabsContainer.value.children[packIdx - 1] as HTMLElement
    if (tabEl) {
      tabsContainer.value.scrollTo({
        left: tabEl.offsetLeft,
        behavior: 'smooth'
      })
    }
  }
}

function selectTab(packIdx: number) {
  activePackIdx.value = packIdx
  EventBus.trigger('select-active-pack-idx', packIdx)
  scrollToTab(packIdx)
  nextTick(() => {
    const el = document.getElementById(`pack-item-${packIdx}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  })
}

function handleScroll() {
  if (!window.matchMedia('(max-width: 767px)').matches) {
    isVisible.value = false
    return
  }
  const customizerEl = document.getElementById('customily-options')
  if (customizerEl) {
    const rect = customizerEl.getBoundingClientRect()
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--fixed-content-height')) || 50
    isVisible.value = rect.top < headerHeight
  } else {
    isVisible.value = window.scrollY > 300
  }
}

onMounted(() => {
  EventBus.on('select-active-pack-idx', handleSelectActivePackIdx)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  EventBus.off('select-active-pack-idx', handleSelectActivePackIdx)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss">
.leversphere-loop-mobile-preview {
  position: sticky;
  top: var(--fixed-content-height, 0);
  z-index: 101;
  width: 100%;
  height: 0;
  overflow: visible;
  display: block;
  box-sizing: border-box;

  @include media-md-up {
    display: none; // Only show on mobile
  }

  .preview-container {
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translate(-50%, 0);
    width: 100%;
    // max-width: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }

  .customily-pack-tabs-wrapper {
    position: relative;
    width: 100%;
    margin-bottom: 0;
    background-color: #f9fafb;
    padding: 0 12px;
    box-sizing: border-box;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 24px;
      height: 100%;
      background: linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent);
      pointer-events: none;
      z-index: 2;
    }
  }

  .close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    
    svg {
      width: 20px;
      height: 20px;
      color: #9ca3af;
    }
  }

  .zoom-btn {
    position: absolute;
    bottom: 8px;
    right: 48px;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;

    svg {
      width: 20px;
      height: 20px;
      color: #9ca3af;
    }
  }

  .slider-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #fff;
  }

  .small-image-wrapper {
    position: relative;
    width: 240px;
    height: 240px;
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;

    &.has-watermark::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      user-select: none;
      z-index: 2;
      background-image: var(--customily-watermark-image);
      background-repeat: repeat;
      background-size: 120px 80px;
    }
  }

  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .badge-title {
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
  }

  .nav-arrow {
    background: #f3f4f6;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #374151;
    transition: background-color 0.2s;

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: #e5e7eb;
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translate(-50%, -100%);
  opacity: 0;
}
</style>
